import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
// TEMPORARY: Braze push sync — remove when legacy facility app is sunset
import { BrazePushSync } from "@src/mobile/utils/brazePushSync";
import { Platform } from "@src/mobile/utils/platform";
import {
  addPushRegistrationErrorListener,
  addPushRegistrationListener,
  checkPushPermissions,
  type PushNotificationListenerHandle,
  registerPushNotifications,
  requestPushPermissions,
} from "@src/mobile/utils/pushNotifications.adapter";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import { useUpdatePushToken } from "../api/useUpdatePushToken";

const PUSH_TOKEN_STORAGE_KEY_PREFIX = "pushNotificationToken_";
const TOKEN_REREGISTRATION_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

function getPushTokenStorageKey(userId: string): string {
  return `${PUSH_TOKEN_STORAGE_KEY_PREFIX}${userId}`;
}

interface StoredPushToken {
  token: string;
  timestamp: number;
}

const storedPushTokenSchema = z.object({
  token: z.string(),
  timestamp: z.number(),
});

function getStoredToken(userId: string): StoredPushToken | undefined {
  try {
    const stored = localStorage.getItem(getPushTokenStorageKey(userId));
    if (!stored) {
      return undefined;
    }

    return storedPushTokenSchema.parse(JSON.parse(stored));
  } catch {
    return undefined;
  }
}

function storeToken(userId: string, token: string): void {
  const entry: StoredPushToken = { token, timestamp: Date.now() };
  localStorage.setItem(getPushTokenStorageKey(userId), JSON.stringify(entry));
}

function shouldSendToken(userId: string, token: string): boolean {
  const stored = getStoredToken(userId);
  if (!stored) {
    return true;
  }

  const tokenChanged = stored.token !== token; // eslint-disable-line security/detect-possible-timing-attacks
  if (tokenChanged) {
    return true;
  }

  return Date.now() - stored.timestamp > TOKEN_REREGISTRATION_INTERVAL_MS;
}

export type PushPermissionState = "unknown" | "granted" | "denied" | "prompt";

export interface UsePushNotificationRegistrationResult {
  /**
   * Current push permission state.
   * - "unknown": still checking
   * - "granted": user already granted permission
   * - "denied": user permanently denied permission
   * - "prompt": user hasn't been asked yet (or can be re-asked)
   */
  permissionState: PushPermissionState;
  /**
   * Call this to request push permissions and register for notifications.
   * Triggers the native OS permission dialog.
   */
  requestPermissionAndRegister: () => Promise<"granted" | "denied" | undefined>;
}

interface UsePushNotificationRegistrationParams {
  workplaceUserId: string;
  isAdmin: boolean;
}

export function usePushNotificationRegistration(
  params: UsePushNotificationRegistrationParams
): UsePushNotificationRegistrationResult {
  const { workplaceUserId, isAdmin } = params;
  const { mutate: updatePushToken } = useUpdatePushToken({ workplaceUserId });
  const listenersRef = useRef<PushNotificationListenerHandle[]>([]);
  const isMountedRef = useRef(true);
  const [permissionState, setPermissionState] = useState<PushPermissionState>("unknown");

  const handleToken = useCallback(
    (token: string) => {
      if (!workplaceUserId) {
        return;
      }

      // TEMPORARY: Braze push sync — remove when legacy facility app is sunset
      // Register the push token with Braze's native SDK so it's associated
      // with the user set by changeUser, even when the token was already
      // cached by Firebase and onNewToken did not fire.
      if (Platform.isMobile) {
        void BrazePushSync.registerPushToken({ token }).catch((error: unknown) => {
          logError(APP_V2_APP_EVENTS.BRAZE_PUSH_SYNC_FAILURE, { error });
        });
      }

      if (!shouldSendToken(workplaceUserId, token)) {
        return;
      }

      updatePushToken(
        { pushToken: token, platform: Platform.platform },
        {
          onSuccess: () => {
            storeToken(workplaceUserId, token);
            logEvent(APP_V2_USER_EVENTS.PUSH_NOTIFICATION_REGISTERED);
          },
        }
      );
    },
    [updatePushToken, workplaceUserId]
  );

  const setupListenersAndRegister = useCallback(
    async (options?: { isCancelled?: () => boolean }) => {
      const registrationListener = await addPushRegistrationListener((pushToken) => {
        handleToken(pushToken.value);
      });

      const errorListener = await addPushRegistrationErrorListener((error) => {
        logError(APP_V2_APP_EVENTS.PUSH_NOTIFICATION_REGISTRATION_ERROR, {
          error,
        });
      });

      if (options?.isCancelled?.()) {
        void registrationListener.remove();
        void errorListener.remove();
        return;
      }

      listenersRef.current = [registrationListener, errorListener];
      await registerPushNotifications();
    },
    [handleToken]
  );

  const requestPermissionAndRegister = useCallback(async (): Promise<
    "granted" | "denied" | undefined
  > => {
    if (!Platform.isMobile || !workplaceUserId || isAdmin) {
      return undefined;
    }

    try {
      logEvent(APP_V2_USER_EVENTS.PUSH_NOTIFICATION_PERMISSION_REQUESTED);
      const permissionResult = await requestPushPermissions();

      if (permissionResult.receive === "granted") {
        logEvent(APP_V2_USER_EVENTS.PUSH_NOTIFICATION_PERMISSION_GRANTED);
        setPermissionState("granted");
        await setupListenersAndRegister({ isCancelled: () => !isMountedRef.current });
        return "granted";
      } else if (permissionResult.receive === "denied") {
        logEvent(APP_V2_USER_EVENTS.PUSH_NOTIFICATION_PERMISSION_DENIED);
        setPermissionState("denied");
        return "denied";
      } else {
        // User dismissed without choosing (e.g. Android "prompt-with-rationale")
        // Keep state as "prompt" so the soft prompt can re-appear after cooldown
        setPermissionState("prompt");
        return undefined;
      }
    } catch (error) {
      logError(APP_V2_APP_EVENTS.PUSH_NOTIFICATION_REGISTRATION_ERROR, {
        error,
      });
      return undefined;
    }
  }, [isAdmin, setupListenersAndRegister, workplaceUserId]);

  // Check existing permission state on mount (without prompting)
  useEffect(() => {
    if (!Platform.isMobile || !workplaceUserId || isAdmin) {
      return;
    }

    let isCancelled = false;

    void (async () => {
      try {
        const currentPermission = await checkPushPermissions();

        if (isCancelled) {
          return;
        }

        if (currentPermission.receive === "granted") {
          setPermissionState("granted");
          // Already granted — register silently
          await setupListenersAndRegister({ isCancelled: () => isCancelled });
        } else if (currentPermission.receive === "denied") {
          setPermissionState("denied");
        } else {
          // "prompt" or "prompt-with-rationale" — soft prompt should handle this
          setPermissionState("prompt");
        }
      } catch (error) {
        logError(APP_V2_APP_EVENTS.PUSH_NOTIFICATION_REGISTRATION_ERROR, {
          error,
        });
      }
    })();

    return () => {
      isCancelled = true;
      for (const listener of listenersRef.current) {
        void listener.remove();
      }
      listenersRef.current = [];
    };
  }, [isAdmin, setupListenersAndRegister, workplaceUserId]);

  // Separate unmount-only effect for isMountedRef so it doesn't get
  // reset to false on every dependency change of the effect above.
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { permissionState, requestPermissionAndRegister };
}
